import * as React from 'react';
import { IndFlowAssignmentRepository } from '../../../MedicareProAPI/Repositories/Ind/IndFlowAssignmentRepository';
import { UserFlowStageRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowStageRepository';
import { UserFlowRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowRepository';
import { SideTab, SIDETAB_POSITION, SIDETAB_SIZE } from '../SideTab';

type HistoryItem = {
  assignmentId: number;
  stage: { id: number; name: string; color?: string | null; icon?: string | null } | null;
  startAt: string | null;
  endAt: string | null;
  durationSeconds?: number | null;
  movedBy?: string | null;
  flow?: { id: number; name: string };
};

type FlowGroup = {
  flow: { id: number; name: string };
  total: number;
  limit: number;
  offset: number;
  items: HistoryItem[];
};

type GroupedHistoryResponse = {
  indId: number;
  groupBy: 'flow';
  groups: FlowGroup[];
};

type FlatHistoryResponse = {
  indId: number;
  total: number;
  limit: number;
  offset: number;
  items: HistoryItem[];
};

type StageLite = { id: number; name: string };

type FlowLite = { id: number; name: string };

export interface IndFlowHistoryProfileProps {
  indId: number;
  userId: number;
  userAccessToken: string;
  defaultOrder?: 'asc' | 'desc';
  defaultSortBy?: 'startAt' | 'endAt';
  activeOnly?: boolean;
  forProfile?: boolean;
  operatorName?: string;
}

/** ---------- Helpers ---------- */
// Days/Weeks/Years formatter (no hours/mins)
function formatDwY(fromISO?: string | null): string {
  if (!fromISO) return '-';
  const t = Date.parse(fromISO);
  if (!isFinite(t)) return '-';
  const ms = Date.now() - t;
  if (ms < 0) return '-';
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `${weeks}w`;
  const years = Math.floor(days / 365);
  return `${years}y`;
}

// Derive current active stages from grouped history (endAt === null)
// Picks most recent active per flow
function getActiveAssignments(groups: FlowGroup[]): HistoryItem[] {
  const active: HistoryItem[] = [];
  for (const g of groups) {
    const actives = (g.items || []).filter((it) => !it.endAt);

    // newest first
    actives.sort((a, b) => (Date.parse(b.startAt ?? '') || 0) - (Date.parse(a.startAt ?? '') || 0));

    // attach flow so UI can render flowName:stageName
    for (const it of actives) {
      active.push({ ...it, flow: g.flow });
    }
  }
  return active;
}

export const IndFlowHistoryProfile: React.FC<IndFlowHistoryProfileProps> = ({
  indId,
  userId,
  userAccessToken,
  defaultOrder = 'desc',
  defaultSortBy = 'startAt',
  activeOnly,
  forProfile,
  operatorName,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [groups, setGroups] = React.useState<FlowGroup[]>([]);
  const [selectedFlowId, setSelectedFlowId] = React.useState<number | null>(null);
  const [hasMounted, setHasMounted] = React.useState<boolean>(false);
  const [showAllFlows, setShowAllFlows] = React.useState<boolean>(false);
  const [seeAllHistory, setSeeAllHistory] = React.useState<boolean>(false);
  const [sideTabSize, setSideTabSize] = React.useState<SIDETAB_SIZE>(SIDETAB_SIZE.MEDIUM);
  // Side tab state
  const [isOpen, setIsOpen] = React.useState(false);
  const [sideForFlowId, setSideForFlowId] = React.useState<number | null>(null);
  const [sideForAssignmentId, setSideForAssignmentId] = React.useState<number | null>(null);

  // Move/Add/Close form state
  const [moveToStageId, setMoveToStageId] = React.useState<number | ''>('');
  const [addFlowId, setAddFlowId] = React.useState<number | ''>('');
  const [addStageId, setAddStageId] = React.useState<number | ''>('');
  const [actionReason, setActionReason] = React.useState<string>('');

  // If you want to show stage options quickly without refetching metadata,
  // you can inject them as props; here we assume repo can supply lists.
  const [allFlows, setAllFlows] = React.useState<Array<{ id: number; name: string }>>([]);
  const [flowsLoading, setFlowsLoading] = React.useState<boolean>(false);
  const [flowsError, setFlowsError] = React.useState<string | null>(null);

  const [stagesByFlow, setStagesByFlow] = React.useState<Record<number, StageLite[]>>({});
  const [stagesLoading, setStagesLoading] = React.useState<Record<number, boolean>>({});
  const [stagesError, setStagesError] = React.useState<Record<number, string | null>>({});

  const [addToFlowOpen, setAddToFlowOpen] = React.useState<boolean>(false);
  // Paging
  const [perFlowOffset, setPerFlowOffset] = React.useState<Record<number, number>>({});
  const perFlowLimit = 50;

  const repo = React.useMemo(() => {
    const r = new IndFlowAssignmentRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const userFlowStageRepository = React.useMemo(() => {
    const r = new UserFlowStageRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const userFlowRepository = React.useMemo(() => {
    const r = new UserFlowRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const loadAllUserFlows = React.useCallback(async () => {
    setFlowsLoading(true);
    setFlowsError(null);
    try {
      const resp = await userFlowRepository.getUserFlows(userId, {}, null);
      // normalize to [{id, name}]
      const flows: FlowLite[] = resp.getItems().map((f: any) => ({
        id: Number(f.id ?? f.flowId),
        name: String(f.name ?? f.flowName),
      }));
      setAllFlows(flows);
    } catch (e: any) {
      setFlowsError(e?.message || 'Failed to load flows');
    } finally {
      setFlowsLoading(false);
    }
  }, [userFlowRepository, userId]);

  React.useEffect(() => {
    loadAllUserFlows();
  }, [loadAllUserFlows]);

  React.useEffect(() => {
    seeAllHistory ? setSideTabSize(SIDETAB_SIZE.LARGE) : setSideTabSize(SIDETAB_SIZE.MEDIUM);
  }, [seeAllHistory]);

  const getActiveFlowIdsForInd = React.useCallback((): Set<number> => {
    const activeFlowIds = new Set<number>();
    for (const g of groups) {
      // if any item in this group is active, that flow is currently active
      const hasActive = (g.items || []).some((it) => !it.endAt);
      if (hasActive) activeFlowIds.add(g.flow.id);
    }
    return activeFlowIds;
  }, [groups]);

  const getCurrentStageIdForOpen = React.useCallback((): number | null => {
    if (!sideForFlowId || !sideForAssignmentId) return null;
    const group = groups.find((g) => g.flow.id === sideForFlowId);
    const item = group?.items.find((i) => i.assignmentId === sideForAssignmentId);
    return item?.stage?.id ?? null;
  }, [groups, sideForFlowId, sideForAssignmentId]);

  const getCurrentStageNameForOpen = React.useCallback((): string | null => {
    if (!sideForFlowId || !sideForAssignmentId) return null;
    const group = groups.find((g) => g.flow.id === sideForFlowId);
    const item = group?.items.find((i) => i.assignmentId === sideForAssignmentId);
    return item?.stage?.name ?? null;
  }, [groups, sideForFlowId, sideForAssignmentId]);

  const loadStagesForFlow = React.useCallback(
    async (flowId: number) => {
      // cache hit
      if (stagesByFlow[flowId]?.length) return;

      setStagesLoading((prev) => ({ ...prev, [flowId]: true }));
      setStagesError((prev) => ({ ...prev, [flowId]: null }));

      try {
        // Your signature: getUserFlowStages(flowId: number, options = {}, criteria = null)
        const resp = await userFlowStageRepository.getUserFlowStages(
          flowId,
          { userId: userId },
          null
        );
        // Normalize to [{id, name}]
        const stages: StageLite[] = resp.getItems().map((s: any) => ({
          id: Number(s.id ?? s.stageId),
          name: String(s.name ?? s.stageName),
        }));
        setStagesByFlow((prev) => ({ ...prev, [flowId]: stages }));
      } catch (e: any) {
        setStagesError((prev) => ({ ...prev, [flowId]: e?.message || 'Failed to load stages' }));
      } finally {
        setStagesLoading((prev) => ({ ...prev, [flowId]: false }));
      }
    },
    [repo, stagesByFlow]
  );

  // ------- Fetch grouped-by-flow history -------
  const fetchHistory = React.useCallback(
    async (flowId?: number) => {
      setLoading(true);
      setError(null);
      try {
        const params: any = {
          groupBy: 'flow',
          sortBy: defaultSortBy,
          order: defaultOrder,
          perFlowLimit: String(perFlowLimit),
        };
        //if (flowId) params["flowId"] = String(flowId);
        if (activeOnly !== undefined) params['active'] = String(!!activeOnly);

        const json: GroupedHistoryResponse | FlatHistoryResponse = await repo.getIndFlowHistory(
          indId,
          params
        );

        if ('groupBy' in json && json.groupBy === 'flow') {
          const g = json.groups ?? [];
          setGroups(g);
          if (!selectedFlowId && g.length > 0) setSelectedFlowId(g[0].flow.id);
        } else {
          const flat = json as FlatHistoryResponse;
          const pseudo: FlowGroup = {
            flow: { id: -1, name: 'All Flows' },
            total: flat.total,
            limit: flat.limit,
            offset: flat.offset,
            items: flat.items,
          };
          setGroups([pseudo]);
          if (!selectedFlowId) setSelectedFlowId(-1);
        }
        setHasMounted(true);
      } catch (e: any) {
        setError(e?.message || 'Failed to load history');
      } finally {
        setLoading(false);
      }
    },
    [indId, defaultSortBy, defaultOrder, activeOnly, selectedFlowId, repo]
  );

  React.useEffect(() => {
    setGroups([]);
    setSelectedFlowId(null);
    setPerFlowOffset({});
    fetchHistory();
  }, [indId, userId, userAccessToken]);

  React.useEffect(() => {
    if (selectedFlowId) {
      loadStagesForFlow(selectedFlowId);
    }
  }, [selectedFlowId, loadStagesForFlow]);

  // ------- Per-flow paging fetch -------
  const refetchFlowGroup = React.useCallback(
    async (flowId: number, nextOffset: number) => {
      setLoading(true);
      setError(null);
      try {
        const params: any = {
          groupBy: 'flow',
          sortBy: defaultSortBy,
          order: defaultOrder,
          perFlowLimit: String(perFlowLimit),
          perFlowOffset: String(nextOffset),
          flowId: String(flowId),
        };
        if (activeOnly !== undefined) params['active'] = String(!!activeOnly);

        const json: GroupedHistoryResponse = await repo.getIndFlowHistory(indId, params);

        const g = json.groups ?? [];

        setGroups((prev) => {
          const map: Record<number, FlowGroup> = Object.fromEntries(
            prev.map((gr) => [gr.flow.id, gr])
          );
          if (g.length > 0) map[flowId] = g[0];
          return Object.values(map);
        });
        setPerFlowOffset((prev) => ({ ...prev, [flowId]: nextOffset }));
      } catch (e: any) {
        setError(e?.message || 'Failed to load history');
      } finally {
        setLoading(false);
      }
    },
    [indId, defaultSortBy, defaultOrder, activeOnly, repo]
  );

  const flows = React.useMemo(() => groups.map((g) => g.flow), [groups]);

  const activeGroup = React.useMemo(
    () => (selectedFlowId != null ? groups.find((g) => g.flow.id === selectedFlowId) : undefined),
    [groups, selectedFlowId]
  );

  const handlePrev = React.useCallback(() => {
    if (!activeGroup) return;
    const current = perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset ?? 0;
    const next = Math.max(0, current - perFlowLimit);
    if (next !== current) refetchFlowGroup(activeGroup.flow.id, next);
  }, [activeGroup, perFlowOffset, refetchFlowGroup]);

  const handleNext = React.useCallback(() => {
    if (!activeGroup) return;
    const current = perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset ?? 0;
    const next = current + perFlowLimit;
    if (next < activeGroup.total) refetchFlowGroup(activeGroup.flow.id, next);
  }, [activeGroup, perFlowOffset, refetchFlowGroup]);

  /** ---------- Base View: Current stage badges + flows column + timeline ---------- */
  const activeAssignments = React.useMemo(() => getActiveAssignments(groups), [groups]);

  const openSideFor = React.useCallback(
    (flowId: number | null, assignmentId: number | null) => {
      setSideForFlowId(flowId);
      setSideForAssignmentId(assignmentId);
      setMoveToStageId('');
      setAddFlowId('');
      setAddStageId('');
      setActionReason('');
      setIsOpen(true);

      if (flowId) {
        loadStagesForFlow(flowId);
      } else {
        // Add mode: ensure flows are available
        if (!allFlows.length) loadAllUserFlows();
      }
    },
    [loadStagesForFlow]
  );

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    // optional: clear selection to avoid stale UI
    // setSideForFlowId(null);
    // setSideForAssignmentId(null);
  }, []);

  const doMove = React.useCallback(async () => {
    if (!sideForFlowId || !sideForAssignmentId || !moveToStageId) return;
    setLoading(true);
    setError(null);
    try {
      await repo.moveIndFlowAssignment(indId, {
        flowId: sideForFlowId,
        movedBy: operatorName,
        stageId: moveToStageId,
      });

      await fetchHistory(sideForFlowId);
      setIsOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to move individual');
    } finally {
      setLoading(false);
    }
  }, [
    repo,
    indId,
    sideForFlowId,
    sideForAssignmentId,
    moveToStageId,
    actionReason,
    groups,
    fetchHistory,
  ]);

  const doClose = React.useCallback(async () => {
    if (!sideForFlowId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        movedBy: operatorName,
      };
      console.log('close payload', payload);
      await repo.closeIndFlowAssignment?.(indId, sideForAssignmentId, payload);

      await fetchHistory();
      setIsOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to close flow');
    } finally {
      setLoading(false);
    }
  }, [repo, indId, sideForFlowId, actionReason, fetchHistory]);

  const doAdd = React.useCallback(async () => {
    if (!addFlowId || !addStageId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        flowId: addFlowId,
        stageId: addStageId,
        movedBy: operatorName,
      };

      await repo.createIndFlowAssignment(indId, payload);

      await fetchHistory();
      setSelectedFlowId(Number(addFlowId));
      setIsOpen(false);
      setAddToFlowOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to add to flow');
    } finally {
      setLoading(false);
    }
  }, [repo, indId, addFlowId, addStageId, actionReason, fetchHistory]);

  return (
    hasMounted && (
      <div className="d-flex flex-column" style={{ gap: 12 }}>
        {forProfile && (
          <div className="d-flex align-items-center flex-wrap">
            {/* Badges */}
            {activeAssignments.length === 0 && (
              <span
                key="makeNewFlowProfile"
                className="badge badge-pill mr-1 mb-2 p-1 d-inline-block border border-secondary"
                onClick={() => {
                  openSideFor(null, null);
                  setAddToFlowOpen(true);
                  setSeeAllHistory(true);
                }}
                style={{ cursor: 'pointer', fontSize: '9pt' }}
              >
                <i className="mr-1 far fa-plus-circle" />
                Flows
              </span>
            )}

            {/* Collapsed view */}
            {activeAssignments.length > 2 && !showAllFlows && (
              <span
                key="viewAllFlows"
                className="badge badge-pill mr-1 mb-2 p-2 badge-primary d-inline-block"
                onClick={() => setShowAllFlows(true)}
                style={{ cursor: 'pointer', fontSize: '9pt' }}
              >
                <i className="mr-1 far fa-plus-circle" />
                View {activeAssignments.length} Flows
              </span>
            )}

            {/* Visible badges */}
            {(activeAssignments.length < 3 || showAllFlows) && (
              <>
                {activeAssignments.map((a) => {
                  const flowName = a.flow?.name ?? 'Unknown Flow';
                  const stageName = a.stage?.name ?? 'Unknown Stage';
                  const icon = a.stage?.icon ?? 'far fa-dot-circle';
                  const badgeClass = a.stage?.color ? `badge-${a.stage.color}` : 'badge-secondary';

                  return (
                    <span
                      key={a.assignmentId}
                      className={`badge badge-pill mr-1 mb-2 p-2 ${badgeClass} d-inline-block`}
                      onClick={() =>
                        openSideFor(a.flow?.id ?? selectedFlowId ?? -1, a.assignmentId)
                      }
                      title={`${flowName}: ${stageName}`}
                      style={{ cursor: 'pointer', fontSize: '9pt' }}
                    >
                      <i className={`${icon} mr-1`} />
                      {flowName}: {stageName}
                    </span>
                  );
                })}

                {activeAssignments.length > 2 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm ml-1 p-0 align-self-start"
                    onClick={() => setShowAllFlows(false)}
                  >
                    <i className="far fa-eye-slash"></i>
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* ---------- MAIN: Flows list + timeline ---------- */}
        {!forProfile && (
          <div className="d-flex" style={{ gap: 16 }}>
            {/* Left: flows list */}
            <div style={{ minWidth: 220 }} className="border rounded p-2 card h-auto">
              <div className="font-weight-bold mb-2">Flows</div>
              {loading && flows.length === 0 && <div className="small text-muted">Loading…</div>}
              {error && <div className="text-danger small mb-2">{error}</div>}
              {flows.length === 0 && !loading && <div className="small text-muted">No flows.</div>}
              <div className="list-group">
                {flows.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`list-group-item p-2 list-group-item-action ${selectedFlowId === f.id ? 'active' : ''}`}
                    style={{ fontSize: '10pt' }}
                    onClick={() => setSelectedFlowId(f.id)}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
              {/* Quick-add button from left rail */}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={async () => {
                  openSideFor(null, null); // null flow => Add mode
                  if (!allFlows.length) await loadAllUserFlows();
                }}
              >
                + Add to Flow
              </button>
            </div>

            {/* Right: timeline */}
            <div className="flex-grow-1 border rounded p-2 card h-auto">
              <div className="card-body">
                <div className="d-flex align-items-center mb-2">
                  <div className="font-weight-bold">
                    {activeGroup ? activeGroup.flow.name : 'Timeline'}
                  </div>
                  <div className="ml-auto small">
                    {activeGroup && (
                      <>
                        <span className="mr-2">
                          {Math.min(
                            (perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset) +
                              activeGroup.items.length,
                            activeGroup.total
                          )}{' '}
                          of {activeGroup.total}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary mr-1"
                          onClick={handlePrev}
                          disabled={
                            (perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset ?? 0) <= 0
                          }
                        >
                          Prev
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleNext}
                          disabled={
                            (perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset ?? 0) +
                              (activeGroup.items?.length ?? 0) >=
                            activeGroup.total
                          }
                        >
                          Next
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!activeGroup && (
                  <div className="small text-muted">Select a flow to view history.</div>
                )}
                {activeGroup && activeGroup.items.length === 0 && (
                  <div className="small text-muted">No history for this flow.</div>
                )}

                {activeGroup && activeGroup.items.length > 0 && (
                  <ul className="list-unstyled m-0 p-0">
                    {activeGroup.items.map((item) => {
                      const start = item.startAt ? new Date(item.startAt) : null;
                      const end = item.endAt ? new Date(item.endAt) : null;
                      const age = formatDwY(item.startAt);
                      const stageName = item.stage?.name ?? '(unknown stage)';
                      const color = item.stage?.color ? `text-${item.stage.color}` : 'text-muted';
                      const icon = item.stage?.icon || 'far fa-dot-circle';
                      return (
                        <li key={item.assignmentId} className="d-flex align-items-start py-2">
                          {/* Timeline marker */}
                          <div className="mr-2" style={{ width: 14, textAlign: 'center' }}>
                            <i className={`${icon} ${color}`} />
                          </div>
                          {/* Timeline content */}
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <div className="font-weight-bold mr-2">{stageName}</div>
                              <span
                                className="badge badge-light"
                                title={start ? start.toLocaleString() : ''}
                              >
                                {age}
                              </span>
                            </div>
                            <div className="small ">
                              {start ? `Start: ${start.toLocaleString()}` : 'Start: —'}
                              {' • '}
                              {end ? `End: ${end.toLocaleString()}` : 'End: —'}
                              {item.movedBy ? ` • By: ${item.movedBy}` : ''}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        <SideTab
          size={sideTabSize}
          position={SIDETAB_POSITION.RIGHT}
          title={sideForFlowId ? 'Manage Flow' : 'Add to Flow'}
          show={isOpen}
          onClose={handleClose}
        >
          {/* Manage (move/close) when opened for a specific flow */}
          {sideForFlowId && (
            <>
              <div className="font-weight-bold mb-2">Move to stage</div>

              <div className="form-group">
                {stagesLoading[sideForFlowId] && (
                  <div className="small text-muted mb-1">Loading stages…</div>
                )}
                {stagesError[sideForFlowId] && (
                  <div className="text-danger small mb-1">{stagesError[sideForFlowId]}</div>
                )}

                {(() => {
                  const currentId = getCurrentStageIdForOpen();
                  const allStages = stagesByFlow[sideForFlowId] || [];
                  const moveOptions = allStages.filter((s) => s.id !== currentId);

                  return (
                    <>
                      <select
                        className="form-control"
                        value={moveToStageId}
                        onChange={(e) =>
                          setMoveToStageId(e.target.value ? Number(e.target.value) : '')
                        }
                        disabled={
                          !sideForFlowId ||
                          !!stagesLoading[sideForFlowId] ||
                          moveOptions.length === 0
                        }
                      >
                        <option value="">
                          {moveOptions.length ? 'Select stage…' : 'No other stages available'}
                        </option>
                        {moveOptions.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>

                      {/* If nothing to move to, show a helpful nudge */}
                      {moveOptions.length === 0 && (
                        <div className="small text-muted mt-1">
                          Looks like this flow only has one stage (or you’re already in the last
                          available one).
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {moveToStageId && (
                <div className="mb-4">
                  <button className="btn btn-outline-primary " onClick={doMove} disabled={loading}>
                    Move
                  </button>
                </div>
              )}

              <div className="mb-4">
                <div className="font-weight-bold mb-2">Close Stage</div>
                <p className="small text-muted m-0">
                  Closes the current stage and ends the individuals flow journey.
                </p>
                <button
                  className="btn btn-outline-success mt-2"
                  onClick={doClose}
                  disabled={loading}
                >
                  {loading ? 'Closing…' : 'Close'}
                </button>
              </div>
            </>
          )}

          {/* Add to new flow/stage*/}
          <div className="mb-3">
            {!addToFlowOpen ? (
              <button
                className="btn btn-outline-primary"
                onClick={async () => {
                  setAddToFlowOpen(true);
                  if (!allFlows.length) await loadAllUserFlows();
                }}
              >
                Add to Flow
              </button>
            ) : (
              <>
                <div className="font-weight-bold mb-2">Add to Flow</div>

                {/* Loading / error indicators for flows */}
                {flowsLoading && <div className="small text-muted mb-2">Loading flows…</div>}
                {flowsError && <div className="text-danger small mb-2">{flowsError}</div>}

                {(() => {
                  const activeIds = getActiveFlowIdsForInd();
                  const eligibleFlows = allFlows.filter((f) => !activeIds.has(f.id));

                  return (
                    <>
                      <div className="form-group">
                        <label className="small">Flow</label>
                        <select
                          className="form-control"
                          value={addFlowId}
                          onChange={async (e) => {
                            const v = e.target.value ? Number(e.target.value) : '';
                            setAddFlowId(v);
                            setAddStageId('');
                            if (typeof v === 'number') {
                              await loadStagesForFlow(v);
                            }
                          }}
                          disabled={flowsLoading || !!flowsError || eligibleFlows.length === 0}
                        >
                          <option value="">
                            {eligibleFlows.length
                              ? 'Select flow…'
                              : 'No eligible flows (already active in all)'}
                          </option>
                          {eligibleFlows.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="small">Stage</label>
                        {typeof addFlowId === 'number' && stagesLoading[addFlowId] && (
                          <div className="small text-muted mb-1">Loading stages…</div>
                        )}
                        {typeof addFlowId === 'number' && stagesError[addFlowId] && (
                          <div className="text-danger small mb-1">{stagesError[addFlowId]}</div>
                        )}
                        <select
                          className="form-control"
                          value={addStageId}
                          onChange={(e) =>
                            setAddStageId(e.target.value ? Number(e.target.value) : '')
                          }
                          disabled={
                            !addFlowId ||
                            (typeof addFlowId === 'number' && !!stagesLoading[addFlowId])
                          }
                        >
                          <option value="">
                            {addFlowId ? 'Select stage…' : 'Choose a flow first'}
                          </option>
                          {typeof addFlowId === 'number' &&
                            (stagesByFlow[Number(addFlowId)] || []).map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div className="d-flex align-items-center">
                        <button
                          className="btn btn-primary mr-2"
                          onClick={doAdd}
                          disabled={
                            !addFlowId || !addStageId || flowsLoading || !!flowsError || loading
                          }
                        >
                          {loading ? 'Adding…' : 'Add to Flow'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => {
                            setAddToFlowOpen(false);
                            setAddFlowId('');
                            setAddStageId('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {/* Quick history for this flow (only in Manage mode) */}
          {sideForFlowId && !seeAllHistory && (
            <>
              <hr />
              <div className="font-weight-bold mb-2">
                History (this flow){' '}
                <span
                  className="badge badge-pill badge-primary ml-2"
                  onClick={() => {
                    setSeeAllHistory(true);
                    setSelectedFlowId(sideForFlowId);
                  }}
                >
                  See all
                </span>
              </div>
              <ul className="list-unstyled m-0 p-0">
                {(groups.find((g) => g.flow.id === sideForFlowId)?.items ?? []).map((item) => {
                  const start = item.startAt ? new Date(item.startAt) : null;
                  const end = item.endAt ? new Date(item.endAt) : null;
                  const icon = item.stage?.icon || 'far fa-dot-circle';
                  const color = item.stage?.color ? `text-${item.stage.color}` : 'text-muted';
                  return (
                    <li key={item.assignmentId} className="d-flex align-items-start py-2">
                      <div className="mr-2" style={{ width: 14, textAlign: 'center' }}>
                        <i className={`${icon} ${color}`} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <div className="font-weight-bold mr-2">
                            {item.stage?.name ?? '(unknown stage)'}
                          </div>
                          <span
                            className="badge badge-light"
                            title={start ? start.toLocaleString() : ''}
                          >
                            {formatDwY(item.startAt)}
                          </span>
                        </div>
                        <div className="small ">
                          {start ? `Start: ${start.toLocaleString()}` : 'Start: —'} •{' '}
                          {end ? `End: ${end.toLocaleString()}` : 'End: —'}
                          {item.movedBy ? ` • By: ${item.movedBy}` : ''}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {selectedFlowId && seeAllHistory && (
            <div className="d-flex" style={{ gap: 16 }}>
              {/* Left: flows list */}
              <div style={{ minWidth: 220 }} className="border rounded p-2 card h-auto">
                <div className="font-weight-bold mb-2">Flows</div>
                {loading && flows.length === 0 && <div className="small text-muted">Loading…</div>}
                {error && <div className="text-danger small mb-2">{error}</div>}
                {flows.length === 0 && !loading && (
                  <div className="small text-muted">No flows.</div>
                )}
                <div className="list-group">
                  {flows.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`list-group-item list-group-item-action ${selectedFlowId === f.id ? 'active' : ''}`}
                      onClick={() => setSelectedFlowId(f.id)}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: timeline */}
              <div className="flex-grow-1 border rounded p-2 card h-auto">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="font-weight-bold">
                      {activeGroup ? activeGroup.flow.name : 'Timeline'}
                    </div>
                    <div className="ml-auto small">
                      {activeGroup && (
                        <>
                          <span className="mr-2">
                            {Math.min(
                              (perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset) +
                                activeGroup.items.length,
                              activeGroup.total
                            )}{' '}
                            of {activeGroup.total}
                          </span>
                          <button
                            className="btn btn-sm btn-outline-secondary mr-1"
                            onClick={handlePrev}
                            disabled={
                              (perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset ?? 0) <= 0
                            }
                          >
                            Prev
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleNext}
                            disabled={
                              (perFlowOffset[activeGroup.flow.id] ?? activeGroup.offset ?? 0) +
                                (activeGroup.items?.length ?? 0) >=
                              activeGroup.total
                            }
                          >
                            Next
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!activeGroup && (
                    <div className="small text-muted">Select a flow to view history.</div>
                  )}
                  {activeGroup && activeGroup.items.length === 0 && (
                    <div className="small text-muted">No history for this flow.</div>
                  )}

                  {activeGroup && activeGroup.items.length > 0 && (
                    <ul className="list-unstyled m-0 p-0">
                      {activeGroup.items.map((item) => {
                        const start = item.startAt ? new Date(item.startAt) : null;
                        const end = item.endAt ? new Date(item.endAt) : null;
                        const age = formatDwY(item.startAt);
                        const stageName = item.stage?.name ?? '(unknown stage)';
                        const color = item.stage?.color ? `text-${item.stage.color}` : 'text-muted';
                        const icon = item.stage?.icon || 'far fa-dot-circle';
                        return (
                          <li key={item.assignmentId} className="d-flex align-items-start py-2">
                            {/* Timeline marker */}
                            <div className="mr-2" style={{ width: 14, textAlign: 'center' }}>
                              <i className={`${icon} ${color}`} />
                            </div>
                            {/* Timeline content */}
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center">
                                <div className="font-weight-bold mr-2">{stageName}</div>
                                <span
                                  className="badge badge-light"
                                  title={start ? start.toLocaleString() : ''}
                                >
                                  {age}
                                </span>
                              </div>
                              <div className="small text-muted">
                                {start ? `Start: ${start.toLocaleString()}` : 'Start: —'}
                                {' • '}
                                {end ? `End: ${end.toLocaleString()}` : 'End: —'}
                                {item.movedBy ? ` • By: ${item.movedBy}` : ''}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* ---- CONTENT END ---- */}
        </SideTab>
      </div>
    )
  );
};
